import { useState, useRef, useEffect } from 'react'
import { CaretDown, Check } from '@phosphor-icons/react'
import { EnumIcon, type IconEntry } from '@/lib/icons'

interface Option {
  value: string
  label: string
  iconKey?: string
}

interface IconSelectProps {
  value: string
  onChange: (value: string) => void
  options: Option[]
  iconMap?: Record<string, IconEntry>
  placeholder?: string
  id?: string
}

export function IconSelect({
  value,
  onChange,
  options,
  iconMap,
  placeholder = '— Wybierz —',
  id,
}: IconSelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const selected = options.find((o) => o.value === value)

  return (
    <div ref={ref} className="relative" id={id}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="form-input w-full flex items-center justify-between gap-sm text-left cursor-pointer"
      >
        <span className="flex items-center gap-xs min-w-0">
          {selected ? (
            <>
              {iconMap && (selected.iconKey || selected.value) && (
                <EnumIcon entry={iconMap[selected.iconKey ?? selected.value]} size={16} />
              )}
              <span className="truncate">{selected.label}</span>
            </>
          ) : (
            <span className="text-frame/60">{placeholder}</span>
          )}
        </span>
        <CaretDown weight="bold" size={12} className="text-frame shrink-0" />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-[2px] z-50 border border-frame bg-paper shadow-page overflow-y-auto"
          style={{ maxHeight: '14rem' }}
        >
          {placeholder && (
            <div
              className="flex items-center gap-xs px-md py-xs cursor-pointer text-sm text-frame/60 hover:bg-paper-dark transition-colors"
              onClick={() => { onChange(''); setOpen(false) }}
            >
              {placeholder}
            </div>
          )}
          {options.filter((o) => o.value !== '').map((opt) => {
            const isSelected = opt.value === value
            return (
              <div
                key={opt.value}
                className={`flex items-center gap-xs px-md py-xs cursor-pointer text-sm transition-colors ${
                  isSelected ? 'bg-botanical-light text-ink' : 'text-ink hover:bg-paper-dark'
                }`}
                onClick={() => { onChange(opt.value); setOpen(false) }}
              >
                {iconMap && (
                  <EnumIcon entry={iconMap[opt.iconKey ?? opt.value]} size={16} />
                )}
                <span className="flex-1 truncate">{opt.label}</span>
                {isSelected && (
                  <Check weight="bold" size={12} className="text-botanical shrink-0" />
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
